import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import type { MeResponse, PermissionKey } from '@bm/contracts';
import { api, setActiveOrg } from './api';

interface AuthContextValue {
  me: MeResponse | null;
  loading: boolean;
  refresh: () => Promise<MeResponse | null>;
  logout: () => Promise<void>;
  selectOrg: (organizationId: string) => Promise<void>;
  can: (permission: PermissionKey) => boolean;
  /** Ends "Anzeigen als" (Plattform › Benutzeraccounts) and restores the
   * platform admin's own session — see `ImpersonationBanner.tsx`. */
  exitImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api<MeResponse>('/auth/me');
      } catch {
        return null;
      }
    },
    staleTime: 30_000,
  });

  const me = data ?? null;

  useEffect(() => {
    setActiveOrg(me?.activeOrganizationId ?? null);
  }, [me?.activeOrganizationId]);

  // resetQueries (not invalidateQueries) — a reset clears the cached `data`
  // immediately and refetches, so no component can briefly read the PREVIOUS
  // session's `me` while the new one is in flight. That gap is exactly how a
  // same-tab admin→member login used to bounce the new user to /members: the
  // still-cached admin `me` (club_admin) satisfied DashboardPage's redirect
  // check for an instant before the fresh, correct `me` arrived.
  const refresh = useCallback(async () => {
    await qc.resetQueries({ queryKey: ['me'] });
    return qc.getQueryData<MeResponse | null>(['me']) ?? null;
  }, [qc]);

  const logout = useCallback(async () => {
    await api('/auth/logout', { method: 'POST' });
    await qc.resetQueries({ queryKey: ['me'] });
  }, [qc]);

  // Same reset-not-invalidate reasoning as `refresh()` above — the admin's
  // own `me` must never be visible even for an instant while the exit
  // request is in flight. `finally`, not `then`: if the admin's original
  // session already expired, the API call itself throws (401
  // `admin_session_expired`) but has already cleared the session cookies —
  // resetting `me` regardless is what actually reflects that.
  const exitImpersonation = useCallback(async () => {
    try {
      await api('/auth/impersonate/exit', { method: 'POST' });
    } finally {
      await qc.resetQueries({ queryKey: ['me'] });
    }
  }, [qc]);

  const selectOrg = useCallback(
    async (organizationId: string) => {
      await api('/auth/select-organization', { method: 'POST', body: { organizationId } });
      setActiveOrg(organizationId);
      // resetQueries (not invalidateQueries) for everything except `['me']`
      // below — `activeOrgId` (module state in `./api`) is a single shared
      // variable every `api()` call reads fresh at dispatch time, not
      // something captured per query. A query still mounted under the
      // PREVIOUS org's cache key at this exact instant (e.g. SeasonPage's
      // `['seasons']`) has its background refetch triggered by this same
      // call too, but dispatched AFTER `setActiveOrg` already ran above —
      // so it fetches the NEW org's data and writes it into a cache entry a
      // component may still be reading as "the previous org's data, just
      // revalidating" (`invalidateQueries` keeps showing the old value while
      // refetching). A page that also carries derived local state keyed off
      // that list (e.g. SeasonPage's auto-selected `seasonId`) can then lock
      // onto that transiently-wrong value and never self-correct, showing
      // no matches until a manual reload. `resetQueries` clears the cached
      // data to `undefined` immediately instead, so nothing wrong is ever
      // read meanwhile. `['me']` is excluded and stays on `invalidateQueries`
      // — resetting it would flash `me` to null, and AuthedLayout
      // (router.tsx) treats that as "not logged in" and hard-redirects to
      // /login, which must never happen on a plain org switch.
      await qc.resetQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      await qc.invalidateQueries({ queryKey: ['me'] });
    },
    [qc],
  );

  const can = useCallback(
    (permission: PermissionKey) => !!me?.permissions.includes(permission),
    [me],
  );

  return (
    <AuthContext.Provider value={{ me, loading: isLoading, refresh, logout, selectOrg, can, exitImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
